import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/romEngine";
import {
    X, PaperPlaneTilt, Copy, CheckCircle, CircleNotch, LinkSimple,
    Envelope, ClockClockwise, WarningCircle,
} from "@phosphor-icons/react";

const RECIPIENT_META = {
    adjuster: { label: "Adjuster", blurb: "Send a Not-To-Exceed work authorization request to the loss adjuster." },
    insurer: { label: "Insurance", blurb: "Send a Not-To-Exceed work authorization request to the carrier / insurance contact." },
    client: { label: "Client", blurb: "Send the quote to the property owner / client for acceptance & sign-off." },
};

const STATUS_CHIP = {
    sent: { cls: "chip-orange", label: "Sent · awaiting" },
    viewed: { cls: "chip-navy", label: "Viewed" },
    accepted: { cls: "chip-green", label: "Accepted & signed" },
    changes_requested: { cls: "chip-orange", label: "Changes requested" },
};

function shareUrl(token) {
    return `${window.location.origin}/share/${token}`;
}

export function SendProposalModal({ open, onClose, job, recipientType, clients = [] }) {
    const meta = RECIPIENT_META[recipientType] || RECIPIENT_META.client;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [doc, setDoc] = useState(null);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (!open) return;
        setDoc(null); setErr(""); setCopied(false);
        // Prefill recipient from the linked client/insurer where possible.
        const inputs = job?.inputs || {};
        const wantId = recipientType === "client" ? inputs.clientId : inputs.insurerId;
        const match = clients.find((c) => c.id === wantId);
        setName(match?.name || "");
        setEmail(match?.email || "");
        // Load any prior proposals for this ROM.
        (async () => {
            try {
                const { data } = await endpoints.romProposals(job.id);
                setHistory(data || []);
            } catch { setHistory([]); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, recipientType]);

    if (!open) return null;

    async function generate() {
        setBusy(true); setErr("");
        try {
            const { data } = await endpoints.sendRomProposal(job.id, {
                recipient_type: recipientType,
                recipient_name: name,
                recipient_email: email,
            });
            setDoc(data);
            setHistory((h) => [data, ...h.filter((d) => d.id !== data.id)]);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function copy(token) {
        navigator.clipboard?.writeText(shareUrl(token));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="send-proposal-modal">
            <div className="w-full max-w-lg my-6 bg-white rounded-[8px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--rom-navy)] text-white px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <PaperPlaneTilt size={20} weight="fill" className="text-[var(--rom-orange)]" />
                        <div>
                            <div className="font-display font-bold text-base leading-tight">Send proposal to {meta.label}</div>
                            <div className="text-[11px] text-white/60">ROM {job?.ref} · {formatCurrency(job?.grandTotal || 0)}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center hover:bg-white/25" data-testid="send-proposal-close"><X size={16} /></button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-sm text-[var(--rom-text-2)]">{meta.blurb}</p>

                    {!doc ? (
                        <>
                            <div>
                                <label className="label">{meta.label} name</label>
                                <input className="input" value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder={`e.g. ${meta.label} contact`} data-testid="send-recipient-name" />
                            </div>
                            <div>
                                <label className="label">{meta.label} email <span className="text-[var(--rom-text-3)] font-normal">(for email — coming soon)</span></label>
                                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="name@example.com" data-testid="send-recipient-email" />
                            </div>
                            <div className="text-[11px] text-[var(--rom-text-2)] bg-[var(--rom-orange-soft)]/50 border border-[var(--rom-orange)]/30 rounded px-3 py-2 flex items-start gap-2">
                                <Envelope size={14} weight="bold" className="text-[var(--rom-orange-dark)] mt-0.5 shrink-0" />
                                <span>Email delivery is being wired up. For now we'll generate a secure link you can send manually — the recipient can <b>view, accept &amp; digitally sign</b>, or <b>request changes</b>.</span>
                            </div>
                            {err && <div className="text-sm text-[var(--rom-danger)]" data-testid="send-error">{err}</div>}
                            <button className="btn btn-primary w-full justify-center" onClick={generate} disabled={busy} data-testid="send-generate-link">
                                {busy ? <CircleNotch size={15} className="animate-spin" /> : <LinkSimple size={15} weight="bold" />}
                                {busy ? "Generating…" : "Generate secure link"}
                            </button>
                        </>
                    ) : (
                        <div className="space-y-3" data-testid="send-link-ready">
                            <div className="flex items-center gap-2 text-[var(--rom-green)] font-semibold text-sm">
                                <CheckCircle size={18} weight="fill" /> Link ready to share
                            </div>
                            <div className="flex items-center gap-2">
                                <input readOnly className="input font-mono text-xs flex-1" value={shareUrl(doc.share_link)} data-testid="send-link-value" />
                                <button className="btn btn-ghost btn-sm shrink-0" onClick={() => copy(doc.share_link)} data-testid="send-copy-link">
                                    {copied ? <CheckCircle size={14} weight="bold" className="text-[var(--rom-green)]" /> : <Copy size={14} weight="bold" />}
                                    {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <a href={shareUrl(doc.share_link)} target="_blank" rel="noreferrer" className="text-xs text-[var(--rom-navy)] font-semibold hover:underline inline-flex items-center gap-1" data-testid="send-preview-link">
                                Preview what the {meta.label.toLowerCase()} sees →
                            </a>
                        </div>
                    )}

                    {history.length > 0 && (
                        <div className="border-t border-[var(--rom-border)] pt-3">
                            <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-2)] font-semibold mb-2 flex items-center gap-1.5">
                                <ClockClockwise size={13} weight="bold" /> Shared proposals
                            </div>
                            <div className="space-y-2" data-testid="send-history">
                                {history.map((d) => {
                                    const sc = STATUS_CHIP[d.status] || STATUS_CHIP.sent;
                                    const rt = RECIPIENT_META[d.payload?.recipient_type]?.label || "Client";
                                    const cr = d.payload?.change_request;
                                    return (
                                        <div key={d.id} className="text-xs bg-slate-50 rounded px-3 py-2 border border-[var(--rom-border)]">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold">{rt}{d.payload?.recipient_name ? ` · ${d.payload.recipient_name}` : ""}</span>
                                                <span className={`chip ${sc.cls}`} style={{ padding: "1px 6px" }}>{sc.label}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <button className="text-[var(--rom-navy)] font-semibold hover:underline inline-flex items-center gap-1" onClick={() => copy(d.share_link)}>
                                                    <Copy size={12} weight="bold" /> Copy link
                                                </button>
                                            </div>
                                            {cr?.message && (
                                                <div className="mt-1.5 text-[var(--rom-orange-dark)] bg-[var(--rom-orange-soft)]/60 rounded px-2 py-1.5 flex items-start gap-1.5">
                                                    <WarningCircle size={13} weight="bold" className="mt-0.5 shrink-0" />
                                                    <span><b>Requested changes:</b> {cr.message}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
